/**
 * Planning Engine Module — Service layer (Supabase I/O)
 * v21.1: Global Planning Mode, Item Planning Basis, Decomposition Maturity
 * v22:   Team Participation Requirements
 *
 * Computed fields (effectiveCapacityHours, workUnitCapacity) are derived here — never stored.
 */

import { getSupabaseClient } from './supabaseClient';
import { resolveCanonicalItemPoolMemberIds } from './itemPoolMembershipSync';
import { runPlanningEngineWithBands } from './planningViabilityBands';
import {
  resolvePlannability,
  type ScopePlannability,
} from './planningScopePlannability';
import {
  deriveRosterCapacityRows,
  mergeCapacityRows,
  scenarioUsesRoster,
  type DerivedRosterPool,
} from './planningRoster';
import { getRosterSupply } from './planningRosterService';
import { buildQualificationInput } from './planningRequirementsService';
import { readStoredEngineOutput } from './planningStoredDecisions';
import {
  buildPlanningSyncFromLiveItems,
  buildScopedPlanningEngineInput,
  type ItemRelationshipGroupLike,
  type ItemRelationshipGroupMembershipLike,
  buildWorkUnitSyncFromLiveItems,
  isSyncedWorkUnitNotes,
  itemToPriority,
  PLANNING_SYNC_DEP_NOTE_PREFIX,
  resolveItemEffortCapacityUnits,
} from './itemToPlanningAdapter';
import { resolveRecipeIdForWorkType } from './executionRecipeService';
import { fetchItemLockState } from './itemVersionsService';
import { ITEM_PLAN_AMENDMENT_HEADER } from './itemCycleContext';
import type { Item } from '../types/prioritization/lifecycle';
import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { getItemEffortBandHours } from '../stores/prioritization/planningDefaultsStore';
import type {
  Scenario,
  Priority,
  WorkUnit,
  Dependency,
  PriorityDependency,
  CapacityModelRow,
  PriorityCapacityRow,
  TeamParticipation,
  PlanningEngineInput,
  PlanningEngineOutput,
  PlanRecord,
  PriorityPlanRecord,
  ExecutiveSummary,
} from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// Helpers — DB row → domain object mappers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toScenario(row: Record<string, any>): Scenario {
  return {
    id: row.id,
    projectId: row.project_id,
    planCycleId: row.plan_cycle_id ?? null,
    name: row.name,
    active: row.active,
    planningMode: row.planning_mode ?? 'priority_estimate',
    planningWeeks: row.planning_weeks,
    capacityBufferPercent: row.capacity_buffer_percent,
    uncertaintyBasis: row.uncertainty_basis === 'work_type' ? 'work_type' : 'buffer',
    defaultWorkUnitHours: row.default_work_unit_hours,
    includeNotReady: row.include_not_ready,
    includeBlocked: row.include_blocked,
    notes: row.notes ?? null,
    horizonStartDate: row.horizon_start_date ?? null,
    horizonEndDate: row.horizon_end_date ?? null,
    bucketGranularity: row.bucket_granularity ?? 'week',
    timePhasedEnabled: row.time_phased_enabled ?? true,
    inputSourceType: row.input_source_type ?? null,
    inputSourceId: row.input_source_id ?? null,
    inputSourceName: row.input_source_name ?? null,
    capacitySource: row.capacity_source ?? 'manual',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPriority(row: Record<string, any>): Priority {
  return {
    id: row.id,
    projectId: row.project_id,
    scenarioId: row.scenario_id ?? null,
    name: row.name,
    category: row.category ?? null,
    altitudeBand: row.altitude_band ?? null,
    track: row.track ?? null,
    itemType: row.item_type ?? null,
    source: row.source ?? null,
    confidence: row.confidence ?? null,
    strategicObjective: row.strategic_objective ?? null,
    accountableLeader: row.accountable_leader ?? null,
    forceRank: row.force_rank ?? null,
    aboveTheLine: row.above_the_line ?? null,
    valueScore: row.value_score ?? null,
    riskReduction: row.risk_reduction ?? null,
    complianceFlag: row.compliance_flag ?? null,
    executiveImperative: row.executive_imperative ?? null,
    costOfDelay: row.cost_of_delay ?? null,
    impactScore: row.impact_score ?? null,
    effortScore: row.effort_score ?? null,
    cognitiveComplexity: row.cognitive_complexity ?? null,
    workDefinitionStatus: row.work_definition_status ?? 'not_decomposed',
    itemPlanningBasis: row.item_planning_basis ?? 'priority_estimate',
    priorityLevelWuEstimate: row.priority_level_wu_estimate ?? null,
    remainingWuEstimate: row.remaining_wu_estimate ?? null,
    priorityResourceType: row.priority_resource_type ?? 'engineering',
    notes: row.notes ?? null,
    targetLaunchStart: row.target_launch_start ?? null,
    targetLaunchEnd: row.target_launch_end ?? null,
    launchProfileId: row.launch_profile_id ?? null,
    owningOrg: row.owning_org ?? null,
    owningOrgUnitId: row.owning_org_unit_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toWorkUnit(row: Record<string, any>): WorkUnit {
  return {
    id: row.id,
    projectId: row.project_id,
    priorityId: row.priority_id ?? null,
    parentWorkUnitId: row.parent_work_unit_id ?? null,
    title: row.title,
    description: row.description ?? null,
    workProfile: row.work_profile ?? 'unknown',
    requiredCapability: row.required_capability ?? null,
    assignedOwner: row.assigned_owner ?? null,
    effortEstimateHours: row.effort_estimate_hours ?? 0,
    durationEstimateDays: row.duration_estimate_days ?? null,
    countedEffortHours: row.counted_effort_hours ?? null,
    resolvedRecipeId: row.resolved_recipe_id ?? null,
    state: row.state ?? 'backlog',
    assumptionFlag: row.assumption_flag ?? null,
    riskLevel: row.risk_level ?? null,
    milestone: row.milestone ?? null,
    includedInPlan: row.included_in_plan ?? null,
    constraintAdjustedRank: row.constraint_adjusted_rank ?? null,
    rankDelta: row.rank_delta ?? null,
    externalIssueKey: row.external_issue_key ?? null,
    sourceId: row.source_id ?? null,
    sourceType: row.source_type ?? null,
    sourceUrl: row.source_url ?? null,
    customerName: row.customer_name ?? null,
    requestedBy: row.requested_by ?? null,
    requestedByOrg: row.requested_by_org ?? null,
    systemsTouched: row.systems_touched ?? null,
    workflowsTouched: row.workflows_touched ?? null,
    personasImpacted: row.personas_impacted ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toDependency(row: Record<string, any>): Dependency {
  return {
    id: row.id,
    projectId: row.project_id,
    fromWorkUnitId: row.from_work_unit_id,
    toWorkUnitId: row.to_work_unit_id,
    dependencyType: row.dependency_type ?? 'Hard',
    blocking: row.blocking ?? true,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPriorityDependency(row: Record<string, any>): PriorityDependency {
  return {
    id: row.id,
    projectId: row.project_id,
    fromPriorityId: row.from_priority_id,
    toPriorityId: row.to_priority_id,
    dependencyType: row.dependency_type ?? 'Hard',
    blocking: row.blocking ?? true,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toTeamParticipation(row: Record<string, any>): TeamParticipation {
  const periods = Array.isArray(row.period_commitments) ? row.period_commitments : [];
  return {
    id: row.id,
    projectId: row.project_id,
    priorityId: row.priority_id ?? null,
    workUnitId: row.work_unit_id ?? null,
    requiredTeam: row.required_team,
    requiredOrgUnitId: row.required_org_unit_id ?? null,
    requiredCapability: row.required_capability ?? null,
    capacityRequiredWu: row.capacity_required_wu ?? null,
    capacityConfirmed: row.capacity_confirmed ?? 'no',
    capabilityPresent: row.capability_present ?? 'no',
    committed: row.committed ?? 'no',
    owner: row.owner ?? null,
    ownerUserId: row.owner_user_id ?? null,
    riskIfUnavailable: row.risk_if_unavailable ?? null,
    notes: row.notes ?? null,
    committedWindowStart: row.committed_window_start ?? null,
    committedWindowEnd: row.committed_window_end ?? null,
    participationOwnerId: row.participation_owner_id ?? null,
    participationDeadline: row.participation_deadline ?? null,
    requestStatus: row.request_status ?? 'requested',
    periodCommitments: periods.map((p: Record<string, unknown>) => ({
      bucketStart: String(p.bucketStart ?? p.bucket_start ?? ''),
      bucketEnd: p.bucketEnd != null || p.bucket_end != null
        ? String(p.bucketEnd ?? p.bucket_end)
        : undefined,
      hours: p.hours != null ? Number(p.hours) : null,
      status: (p.status as TeamParticipation['requestStatus']) ?? 'requested',
    })).filter((p: { bucketStart: string }) => !!p.bucketStart),
    requestedBy: row.requested_by ?? null,
    requestedAt: row.requested_at ?? null,
    respondedBy: row.responded_by ?? null,
    respondedAt: row.responded_at ?? null,
    responseNote: row.response_note ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function deriveCapacityFields(
  weeklyCapacityHours: number,
  scenario: Scenario,
): { effectiveCapacityHours: number; workUnitCapacity: number } {
  // The buffer is this scenario's uncertainty allowance, so it applies only when
  // the buffer IS the basis. Under 'work_type' the allowance lives in the
  // recipes' multipliers on the demand side instead, and taking both would
  // charge the same uncertainty twice.
  const bufferApplies = scenario.uncertaintyBasis !== 'work_type';
  const effectiveCapacityHours =
    weeklyCapacityHours * scenario.planningWeeks * (bufferApplies ? 1 - scenario.capacityBufferPercent : 1);
  const workUnitCapacity =
    scenario.defaultWorkUnitHours > 0
      ? effectiveCapacityHours / scenario.defaultWorkUnitHours
      : 0;
  return { effectiveCapacityHours, workUnitCapacity };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCapacityRow(row: Record<string, any>, scenario: Scenario): CapacityModelRow {
  const { effectiveCapacityHours, workUnitCapacity } = deriveCapacityFields(
    row.weekly_capacity_hours,
    scenario,
  );
  return {
    id: row.id,
    projectId: row.project_id,
    scenarioId: row.scenario_id,
    resourceType: row.resource_type,
    resourceName: row.resource_name ?? null,
    weeklyCapacityHours: row.weekly_capacity_hours,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    effectiveCapacityHours,
    workUnitCapacity,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPriorityCapacityRow(row: Record<string, any>, scenario: Scenario): PriorityCapacityRow {
  const { effectiveCapacityHours, workUnitCapacity } = deriveCapacityFields(
    row.weekly_capacity_hours,
    scenario,
  );
  return {
    id: row.id,
    projectId: row.project_id,
    scenarioId: row.scenario_id,
    resourceType: row.resource_type,
    resourceName: row.resource_name ?? null,
    weeklyCapacityHours: row.weekly_capacity_hours,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    effectiveCapacityHours,
    workUnitCapacity,
  };
}

// Fallback scenario shape used when a join is missing
function fallbackScenario(scenarioId: string, projectId: string): Scenario {
  return {
    id: scenarioId,
    projectId,
    planCycleId: null,
    name: '',
    active: false,
    planningMode: 'priority_estimate',
    planningWeeks: 12,
    capacityBufferPercent: 0.2,
    uncertaintyBasis: 'buffer' as const,
    defaultWorkUnitHours: 8,
    includeNotReady: false,
    includeBlocked: false,
    notes: null,
    horizonStartDate: null,
    horizonEndDate: null,
    bucketGranularity: 'week',
    timePhasedEnabled: false,
    capacitySource: 'manual',
    createdAt: '',
    updatedAt: '',
  };
}

// ---------------------------------------------------------------------------
// Scenario DB mappers
// ---------------------------------------------------------------------------

function scenarioToDb(data: Partial<Scenario>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.planCycleId !== undefined) db.plan_cycle_id = data.planCycleId;
  if (data.name !== undefined) db.name = data.name;
  if (data.active !== undefined) db.active = data.active;
  if (data.planningMode !== undefined) db.planning_mode = data.planningMode;
  if (data.planningWeeks !== undefined) db.planning_weeks = data.planningWeeks;
  if (data.capacityBufferPercent !== undefined) db.capacity_buffer_percent = data.capacityBufferPercent;
  if (data.uncertaintyBasis !== undefined) db.uncertainty_basis = data.uncertaintyBasis;
  if (data.defaultWorkUnitHours !== undefined) db.default_work_unit_hours = data.defaultWorkUnitHours;
  if (data.includeNotReady !== undefined) db.include_not_ready = data.includeNotReady;
  if (data.includeBlocked !== undefined) db.include_blocked = data.includeBlocked;
  if (data.notes !== undefined) db.notes = data.notes;
  if (data.horizonStartDate !== undefined) db.horizon_start_date = data.horizonStartDate;
  if (data.horizonEndDate !== undefined) db.horizon_end_date = data.horizonEndDate;
  if (data.bucketGranularity !== undefined) db.bucket_granularity = data.bucketGranularity;
  if (data.timePhasedEnabled !== undefined) db.time_phased_enabled = data.timePhasedEnabled;
  if (data.inputSourceType !== undefined) db.input_source_type = data.inputSourceType;
  if (data.inputSourceId !== undefined) db.input_source_id = data.inputSourceId;
  if (data.inputSourceName !== undefined) db.input_source_name = data.inputSourceName;
  if (data.capacitySource !== undefined) db.capacity_source = data.capacitySource;
  return db;
}

/**
 * Link a planning scenario to a governance plan cycle.
 * After linking, the "Propose" flow pre-fills the cycle.
 */
export async function linkScenarioPlanCycle(
  scenarioId: string,
  planCycleId: string | null,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update({ plan_cycle_id: planCycleId })
    .eq('id', scenarioId);
  if (error) throw error;
}

function priorityToDb(data: Partial<Priority>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.scenarioId !== undefined) db.scenario_id = data.scenarioId;
  if (data.name !== undefined) db.name = data.name;
  if (data.category !== undefined) db.category = data.category;
  if (data.altitudeBand !== undefined) db.altitude_band = data.altitudeBand;
  if (data.track !== undefined) db.track = data.track;
  if (data.itemType !== undefined) db.item_type = data.itemType;
  if (data.source !== undefined) db.source = data.source;
  if (data.confidence !== undefined) db.confidence = data.confidence;
  if (data.strategicObjective !== undefined) db.strategic_objective = data.strategicObjective;
  if (data.accountableLeader !== undefined) db.accountable_leader = data.accountableLeader;
  if (data.forceRank !== undefined) db.force_rank = data.forceRank;
  if (data.aboveTheLine !== undefined) db.above_the_line = data.aboveTheLine;
  if (data.valueScore !== undefined) db.value_score = data.valueScore;
  if (data.riskReduction !== undefined) db.risk_reduction = data.riskReduction;
  if (data.complianceFlag !== undefined) db.compliance_flag = data.complianceFlag;
  if (data.executiveImperative !== undefined) db.executive_imperative = data.executiveImperative;
  if (data.costOfDelay !== undefined) db.cost_of_delay = data.costOfDelay;
  if (data.impactScore !== undefined) db.impact_score = data.impactScore;
  if (data.effortScore !== undefined) db.effort_score = data.effortScore;
  if (data.cognitiveComplexity !== undefined) db.cognitive_complexity = data.cognitiveComplexity;
  if (data.workDefinitionStatus !== undefined) db.work_definition_status = data.workDefinitionStatus;
  if (data.itemPlanningBasis !== undefined) db.item_planning_basis = data.itemPlanningBasis;
  if (data.priorityLevelWuEstimate !== undefined) db.priority_level_wu_estimate = data.priorityLevelWuEstimate;
  if (data.remainingWuEstimate !== undefined) db.remaining_wu_estimate = data.remainingWuEstimate;
  if (data.priorityResourceType !== undefined) db.priority_resource_type = data.priorityResourceType;
  if (data.notes !== undefined) db.notes = data.notes;
  if (data.targetLaunchStart !== undefined) db.target_launch_start = data.targetLaunchStart;
  if (data.targetLaunchEnd !== undefined) db.target_launch_end = data.targetLaunchEnd;
  if (data.launchProfileId !== undefined) db.launch_profile_id = data.launchProfileId;
  if (data.owningOrg !== undefined) db.owning_org = data.owningOrg;
  if (data.owningOrgUnitId !== undefined) db.owning_org_unit_id = data.owningOrgUnitId;
  return db;
}

function workUnitToDb(data: Partial<WorkUnit>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.priorityId !== undefined) db.priority_id = data.priorityId;
  if (data.parentWorkUnitId !== undefined) db.parent_work_unit_id = data.parentWorkUnitId;
  if (data.title !== undefined) db.title = data.title;
  if (data.description !== undefined) db.description = data.description;
  if (data.workProfile !== undefined) db.work_profile = data.workProfile;
  if (data.requiredCapability !== undefined) db.required_capability = data.requiredCapability;
  if (data.assignedOwner !== undefined) db.assigned_owner = data.assignedOwner;
  if (data.effortEstimateHours !== undefined) db.effort_estimate_hours = data.effortEstimateHours;
  if (data.durationEstimateDays !== undefined) db.duration_estimate_days = data.durationEstimateDays;
  if (data.countedEffortHours !== undefined) db.counted_effort_hours = data.countedEffortHours;
  if (data.state !== undefined) db.state = data.state;
  if (data.assumptionFlag !== undefined) db.assumption_flag = data.assumptionFlag;
  if (data.riskLevel !== undefined) db.risk_level = data.riskLevel;
  if (data.milestone !== undefined) db.milestone = data.milestone;
  if (data.includedInPlan !== undefined) db.included_in_plan = data.includedInPlan;
  if (data.constraintAdjustedRank !== undefined) db.constraint_adjusted_rank = data.constraintAdjustedRank;
  if (data.rankDelta !== undefined) db.rank_delta = data.rankDelta;
  if (data.externalIssueKey !== undefined) db.external_issue_key = data.externalIssueKey;
  if (data.sourceId !== undefined) db.source_id = data.sourceId;
  if (data.sourceType !== undefined) db.source_type = data.sourceType;
  if (data.sourceUrl !== undefined) db.source_url = data.sourceUrl;
  if (data.customerName !== undefined) db.customer_name = data.customerName;
  if (data.requestedBy !== undefined) db.requested_by = data.requestedBy;
  if (data.requestedByOrg !== undefined) db.requested_by_org = data.requestedByOrg;
  if (data.systemsTouched !== undefined) db.systems_touched = data.systemsTouched;
  if (data.workflowsTouched !== undefined) db.workflows_touched = data.workflowsTouched;
  if (data.personasImpacted !== undefined) db.personas_impacted = data.personasImpacted;
  if (data.notes !== undefined) db.notes = data.notes;
  return db;
}

function dependencyToDb(data: Partial<Dependency>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.fromWorkUnitId !== undefined) db.from_work_unit_id = data.fromWorkUnitId;
  if (data.toWorkUnitId !== undefined) db.to_work_unit_id = data.toWorkUnitId;
  if (data.dependencyType !== undefined) db.dependency_type = data.dependencyType;
  if (data.blocking !== undefined) db.blocking = data.blocking;
  if (data.notes !== undefined) db.notes = data.notes;
  return db;
}

function priorityDependencyToDb(data: Partial<PriorityDependency>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.fromPriorityId !== undefined) db.from_priority_id = data.fromPriorityId;
  if (data.toPriorityId !== undefined) db.to_priority_id = data.toPriorityId;
  if (data.dependencyType !== undefined) db.dependency_type = data.dependencyType;
  if (data.blocking !== undefined) db.blocking = data.blocking;
  if (data.notes !== undefined) db.notes = data.notes;
  return db;
}

function capacityRowToDb(data: Partial<CapacityModelRow>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.scenarioId !== undefined) db.scenario_id = data.scenarioId;
  if (data.resourceType !== undefined) db.resource_type = data.resourceType;
  if (data.resourceName !== undefined) db.resource_name = data.resourceName;
  if (data.weeklyCapacityHours !== undefined) db.weekly_capacity_hours = data.weeklyCapacityHours;
  if (data.notes !== undefined) db.notes = data.notes;
  return db;
}

function teamParticipationToDb(data: Partial<TeamParticipation>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (data.projectId !== undefined) db.project_id = data.projectId;
  if (data.priorityId !== undefined) db.priority_id = data.priorityId;
  if (data.workUnitId !== undefined) db.work_unit_id = data.workUnitId;
  if (data.requiredTeam !== undefined) db.required_team = data.requiredTeam;
  if (data.requiredOrgUnitId !== undefined) db.required_org_unit_id = data.requiredOrgUnitId;
  if (data.requiredCapability !== undefined) db.required_capability = data.requiredCapability;
  if (data.capacityRequiredWu !== undefined) db.capacity_required_wu = data.capacityRequiredWu;
  if (data.capacityConfirmed !== undefined) db.capacity_confirmed = data.capacityConfirmed;
  if (data.capabilityPresent !== undefined) db.capability_present = data.capabilityPresent;
  if (data.committed !== undefined) db.committed = data.committed;
  if (data.owner !== undefined) db.owner = data.owner;
  if (data.ownerUserId !== undefined) db.owner_user_id = data.ownerUserId;
  if (data.riskIfUnavailable !== undefined) db.risk_if_unavailable = data.riskIfUnavailable;
  if (data.notes !== undefined) db.notes = data.notes;
  if (data.committedWindowStart !== undefined) db.committed_window_start = data.committedWindowStart;
  if (data.committedWindowEnd !== undefined) db.committed_window_end = data.committedWindowEnd;
  if (data.participationOwnerId !== undefined) db.participation_owner_id = data.participationOwnerId;
  if (data.participationDeadline !== undefined) db.participation_deadline = data.participationDeadline;
  if (data.requestStatus !== undefined) db.request_status = data.requestStatus;
  if (data.periodCommitments !== undefined) db.period_commitments = data.periodCommitments;
  return db;
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

export async function getScenarios(projectId: string): Promise<Scenario[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toScenario);
}

/**
 * Count planning_priorities rows per scenario, for the given scenario ids.
 * A scenario with 0 rows here and no explicit inputSourceType silently
 * resolves to "All Live Items" (see resolveScenarioBoundEngineInput) — this
 * is how the Scenarios list surfaces that before it surprises anyone.
 */
export async function getScenarioPriorityCounts(
  scenarioIds: string[],
): Promise<Record<string, number>> {
  if (scenarioIds.length === 0) return {};
  const { data, error } = await getSupabaseClient()
    .from('planning_priorities')
    .select('scenario_id')
    .in('scenario_id', scenarioIds);
  if (error) throw error;
  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    const id = (row as { scenario_id: string }).scenario_id;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export async function createScenario(projectId: string, data: Partial<Scenario>): Promise<Scenario> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .insert({ project_id: projectId, ...scenarioToDb(data) })
    .select()
    .single();
  if (error) throw error;
  return toScenario(row);
}

export async function updateScenario(id: string, data: Partial<Scenario>): Promise<Scenario> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update(scenarioToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return toScenario(row);
}

export async function deleteScenario(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('planning_scenarios').delete().eq('id', id);
  if (error) throw error;
}

export async function activateScenario(scenarioId: string, projectId: string): Promise<void> {
  const sb = getSupabaseClient();
  const { error: e1 } = await sb
    .from('planning_scenarios')
    .update({ active: false })
    .eq('project_id', projectId)
    .neq('id', scenarioId);
  if (e1) throw e1;
  const { error: e2 } = await sb
    .from('planning_scenarios')
    .update({ active: true })
    .eq('id', scenarioId);
  if (e2) throw e2;
}

/**
 * Persist the Engine Input Scope that owns this scenario's portfolio.
 * Used so activate / left-nav always restores Keith (or any bound) isolation.
 */
export async function bindScenarioInputSource(
  scenarioId: string,
  source: EngineInputSource,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update({
      input_source_type: source.type,
      input_source_id: source.type === 'live' ? null : source.id,
      input_source_name: source.type === 'live' ? 'All Live Items' : source.name,
    })
    .eq('id', scenarioId);
  if (error) throw error;
}

export const BOUND_INPUT_TYPES = new Set<EngineInputSource['type']>([
  'item_pool',
  'collection',
  'published_priority_list',
  'queue',
  'scenario_portfolio',
]);

/**
 * Resolve the hard scope container for a scenario.
 * Prefer persisted input_source_*; else derive scenario_portfolio from planning_priorities.
 * Returns null when unbound (free exploration — All Live Items allowed).
 */
export async function resolveScenarioBoundEngineInput(
  scenario: Scenario,
): Promise<EngineInputSource | null> {
  const storedType = scenario.inputSourceType as EngineInputSource['type'] | null | undefined;
  if (
    storedType &&
    BOUND_INPUT_TYPES.has(storedType) &&
    scenario.inputSourceId
  ) {
    return {
      type: storedType,
      id: scenario.inputSourceId,
      name: scenario.inputSourceName?.trim() || scenario.name,
    } as Exclude<EngineInputSource, { type: 'live' }>;
  }

  const { data, error } = await getSupabaseClient()
    .from('planning_priorities')
    .select('id')
    .eq('scenario_id', scenario.id)
    .limit(1);
  if (error) {
    console.warn('[resolveScenarioBoundEngineInput]', error.message);
    return null;
  }
  if (!data?.length) return null;

  return {
    type: 'scenario_portfolio',
    id: scenario.id,
    name: scenario.name,
  };
}

export async function duplicateScenario(scenarioId: string): Promise<Scenario> {
  const sb = getSupabaseClient();
  const { data: orig, error: e1 } = await sb
    .from('planning_scenarios')
    .select('*')
    .eq('id', scenarioId)
    .single();
  if (e1) throw e1;

  const { data: newScenario, error: e2 } = await sb
    .from('planning_scenarios')
    .insert({
      project_id: orig.project_id,
      name: `${orig.name} (copy)`,
      active: false,
      planning_mode: orig.planning_mode,
      planning_weeks: orig.planning_weeks,
      capacity_buffer_percent: orig.capacity_buffer_percent,
      uncertainty_basis: orig.uncertainty_basis,
      default_work_unit_hours: orig.default_work_unit_hours,
      include_not_ready: orig.include_not_ready,
      include_blocked: orig.include_blocked,
      notes: orig.notes,
      horizon_start_date: orig.horizon_start_date ?? null,
      horizon_end_date: orig.horizon_end_date ?? null,
      bucket_granularity: orig.bucket_granularity ?? 'week',
      time_phased_enabled: orig.time_phased_enabled ?? false,
    })
    .select()
    .single();
  if (e2) throw e2;

  // Copy work-unit capacity rows
  const { data: capRows, error: e3 } = await sb
    .from('planning_capacity')
    .select('*')
    .eq('scenario_id', scenarioId);
  if (e3) throw e3;

  if (capRows && capRows.length > 0) {
    const newRows = capRows.map((r: Record<string, unknown>) => ({
      project_id: r.project_id,
      scenario_id: newScenario.id,
      resource_type: r.resource_type,
      resource_name: r.resource_name,
      weekly_capacity_hours: r.weekly_capacity_hours,
      notes: r.notes,
    }));
    const { error: e4 } = await sb.from('planning_capacity').insert(newRows);
    if (e4) throw e4;
  }

  // Copy priority capacity rows
  const { data: priCapRows, error: e5 } = await sb
    .from('planning_priority_capacity')
    .select('*')
    .eq('scenario_id', scenarioId);
  if (e5) throw e5;

  if (priCapRows && priCapRows.length > 0) {
    const newRows = priCapRows.map((r: Record<string, unknown>) => ({
      project_id: r.project_id,
      scenario_id: newScenario.id,
      resource_type: r.resource_type,
      resource_name: r.resource_name,
      weekly_capacity_hours: r.weekly_capacity_hours,
      notes: r.notes,
    }));
    const { error: e6 } = await sb.from('planning_priority_capacity').insert(newRows);
    if (e6) throw e6;
  }

  return toScenario(newScenario);
}

// ---------------------------------------------------------------------------
// Priorities
// ---------------------------------------------------------------------------

export async function getPriorities(projectId: string): Promise<Priority[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_priorities')
    .select('*')
    .eq('project_id', projectId)
    .order('force_rank', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []).map(toPriority);
}

/** Planning-specific overrides stored against an item UUID. */
export interface PlanningOverride {
  forceRank: number | null;
  aboveTheLine: boolean | null;
  valueScore: number | null;
  effortScore: number | null;
  priorityLevelWuEstimate: number | null;
  priorityResourceType: string | null;
  notes: string | null;
}

/**
 * Merge a live-item-derived Priority with a planning_priorities overlay row.
 * Overlay fields win when non-null so Save → reload keeps planning edits.
 */
export function applyPlanningOverride(base: Priority, ov: PlanningOverride | undefined): Priority {
  if (!ov) return base;
  return {
    ...base,
    forceRank: ov.forceRank ?? base.forceRank,
    aboveTheLine: ov.aboveTheLine ?? base.aboveTheLine,
    valueScore: ov.valueScore ?? base.valueScore,
    effortScore: ov.effortScore ?? base.effortScore,
    // Prefer an explicit planning estimate (including 0) over the derived item estimate.
    priorityLevelWuEstimate:
      ov.priorityLevelWuEstimate !== null && ov.priorityLevelWuEstimate !== undefined
        ? ov.priorityLevelWuEstimate
        : base.priorityLevelWuEstimate,
    priorityResourceType: ov.priorityResourceType ?? base.priorityResourceType,
    notes: ov.notes ?? base.notes,
  };
}

/**
 * Fetch only the engine-specific fields from planning_priorities.
 * Use this when items are loaded from the items store and planning_priorities
 * is treated as an overlay rather than the primary data source.
 */
export async function getPlanningOverrides(
  projectId: string,
): Promise<Map<string, PlanningOverride>> {
  const { data, error } = await getSupabaseClient()
    .from('planning_priorities')
    .select('id, force_rank, above_the_line, value_score, effort_score, priority_level_wu_estimate, priority_resource_type, notes')
    .eq('project_id', projectId);
  if (error) throw error;
  const map = new Map<string, PlanningOverride>();
  for (const row of data ?? []) {
    map.set(row.id as string, {
      forceRank: (row.force_rank as number | null) ?? null,
      aboveTheLine: (row.above_the_line as boolean | null) ?? null,
      valueScore: (row.value_score as number | null) ?? null,
      effortScore: (row.effort_score as number | null) ?? null,
      priorityLevelWuEstimate: (row.priority_level_wu_estimate as number | null) ?? null,
      priorityResourceType: (row.priority_resource_type as string | null) ?? null,
      notes: (row.notes as string | null) ?? null,
    });
  }
  return map;
}

/**
 * Live-item-derived priorities, overlaid with planning_priorities and
 * filtered to a resolved Input Scope — the same merge PlanningPrioritiesView
 * (the Priorities tab) uses.
 *
 * A scenario bound to an Item Pool (input_source_type: 'item_pool') has no
 * planning_priorities rows of its own — its priorities ARE the live items in
 * that pool. Querying planning_priorities by scenario_id directly (as the
 * Time-Phased Launch Setup and Funding & Costs panels used to) returns
 * nothing for exactly this kind of scenario, showing "No priorities in this
 * scenario yet" even when the scenario has real, resolvable priorities.
 */
export async function getScopedPriorities(
  projectId: string,
  liveItems: Item[],
  scenarioId: string | null,
  scopedItemIds: Set<string> | null,
): Promise<Priority[]> {
  const [overrides, allDbPriorities] = await Promise.all([
    getPlanningOverrides(projectId),
    getPriorities(projectId),
  ]);
  const liveItemIds = new Set(liveItems.map((i) => i.id));
  const bandHours = getItemEffortBandHours();
  const mapped: Priority[] = liveItems
    .filter((item) => item.exit_status !== 'archived')
    .map((item) => {
      const base = itemToPriority(item, scenarioId, bandHours);
      const withProject = base.projectId ? base : { ...base, projectId };
      return applyPlanningOverride(withProject, overrides.get(item.id));
    });
  // Unbound priorities — rows in planning_priorities with no matching live item.
  const unboundPriorities = allDbPriorities.filter((p) => !liveItemIds.has(p.id));
  const combined = [...mapped, ...unboundPriorities];
  return scopedItemIds ? combined.filter((p) => scopedItemIds.has(p.id)) : combined;
}

export async function createPriority(projectId: string, data: Partial<Priority>): Promise<Priority> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_priorities')
    .insert({ project_id: projectId, name: data.name ?? 'New Priority', ...priorityToDb(data) })
    .select()
    .single();
  if (error) throw error;
  return toPriority(row);
}

export async function updatePriority(id: string, data: Partial<Priority>): Promise<Priority> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_priorities')
    .update(priorityToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return toPriority(row);
}

/**
 * Upsert a full planning priority row — used when saving edits to a priority
 * that was derived from a live item (and therefore may not yet have a row in
 * planning_priorities). Falls back to insert if the row doesn't exist.
 */
export async function upsertPriorityOverride(priority: Priority): Promise<Priority> {
  if (!priority.projectId) {
    throw new Error('Cannot save planning priority: missing project_id (workspace).');
  }
  const row = {
    id: priority.id,
    project_id: priority.projectId,
    name: priority.name,
    ...priorityToDb(priority),
  };
  const { data: upserted, error } = await getSupabaseClient()
    .from('planning_priorities')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  if (!upserted) throw new Error('Save failed: planning_priorities upsert returned no row.');

  // Write Item Effort (CU) back to the Prioritization item — never altitude workSize.
  const cu = priority.priorityLevelWuEstimate;
  if (cu != null && Number.isFinite(cu) && cu > 0) {
    const writeEffort = () =>
      getSupabaseClient()
        .from('items')
        .update({
          effort_capacity_units: cu,
          effort_source: 'planner_override',
          updated_at: new Date().toISOString(),
        })
        .eq('id', priority.id);
    const { error: effortError } = await writeEffort();
    if (effortError?.hint === 'item_held_by_plan') {
      // A plan in force holds this item (migration 1019). Someone who may amend that plan can
      // still move the item; the plan keeps the change out until it is re-authorized. For anyone
      // else the estimate stays on this plan's own row and the item keeps its effort.
      const lock = await fetchItemLockState(priority.id).catch(() => null);
      if (lock?.planLock) {
        const { error: amendError } = await writeEffort().setHeader(ITEM_PLAN_AMENDMENT_HEADER, lock.planLock.planCycleId);
        if (amendError && amendError.hint !== 'item_held_by_plan') throw amendError;
      }
    } else if (effortError) {
      throw effortError;
    }
  }

  return toPriority(upserted);
}

export async function deletePriority(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('planning_priorities').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Ensure a planning_priorities row exists for a live-item-derived priority so
 * FK'd overlays (e.g. planning_priority_cost_plans) can attach to it (§4.7 —
 * planning fields are overlays on items.id). No-op when the row already
 * exists; never overwrites existing fields.
 */
export async function ensurePlanningPriorityRow(
  projectId: string,
  priorityId: string,
  name: string,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_priorities')
    .upsert(
      { id: priorityId, project_id: projectId, name },
      { onConflict: 'id', ignoreDuplicates: true },
    );
  if (error) throw error;
}

/**
 * Pure: move `movedId` to 1-based rank `moveToRank` within `list`, sorted by
 * each priority's CURRENT forceRank first (nulls last) as the baseline order.
 * No I/O — safe to unit test directly. Used by both entry points (the Rank
 * number-input and the drag-reorder modal) so the "what does moving to
 * rank N actually mean" logic exists exactly once.
 */
export function computeReorderedByRank(list: Priority[], movedId: string, moveToRank: number): Priority[] {
  const sorted = [...list].sort(
    (a, b) => (a.forceRank ?? Number.MAX_SAFE_INTEGER) - (b.forceRank ?? Number.MAX_SAFE_INTEGER),
  );
  const fromIdx = sorted.findIndex((p) => p.id === movedId);
  if (fromIdx === -1) return sorted;
  const [entry] = sorted.splice(fromIdx, 1);
  const clampedIdx = Math.max(0, Math.min(sorted.length, moveToRank - 1));
  sorted.splice(clampedIdx, 0, entry);
  return sorted;
}

/**
 * Reassign forceRank sequentially (1..N) over an already-reordered list —
 * the correct way to move a priority to a new rank. force_rank is a plain
 * per-row integer with no uniqueness constraint: writing one priority's rank
 * in isolation (the previous behavior) produces duplicate ranks the moment
 * two priorities land on the same number, rather than shifting the rest of
 * the range like a real reorder.
 *
 * orderedPriorities must be the FULL set the rank numbers are relative to —
 * typically whatever the caller's list view currently has visible under the
 * active Input Scope, in the caller's intended final order. Only rows whose
 * rank actually changed are written. Reuses upsertPriorityOverride so a
 * live-item-derived priority with no planning_priorities row yet still saves
 * correctly (a plain UPDATE would silently touch zero rows for it).
 */
export async function reorderPriorities(orderedPriorities: Priority[]): Promise<Priority[]> {
  const results = await Promise.all(
    orderedPriorities.map((p, i) => {
      const nextRank = i + 1;
      if (p.forceRank === nextRank) return p;
      return upsertPriorityOverride({ ...p, forceRank: nextRank });
    }),
  );
  return results;
}

/**
 * Import a published priority list's order into planning_priorities.force_rank.
 *
 * Called when the user points the engine's Input Scope at a published list.
 * Without this, force_rank rows persisted from a previous scope silently win
 * over the newly selected list's order — the engine draws an Above/Below line
 * ordered by last month's ranking while claiming to run against the new list
 * (Mosso demo finding F5). Selecting a published list means "plan against this
 * governed order", so the list's rank is (re)imported at selection time; manual
 * re-ranks made afterwards through reorderPriorities still stick.
 *
 * Rows are upserted so list members with no planning_priorities row yet get
 * one (name sourced from the live item); existing rows keep their name and
 * other overlay fields — only force_rank is rewritten.
 */
export async function reimportForceRanksFromPublishedList(
  projectId: string,
  prioritySetId: string,
): Promise<{ updated: number }> {
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('priority_sets')
    .select('ranked_item_ids, item_ids')
    .eq('id', prioritySetId)
    .maybeSingle();
  if (error) throw error;
  const row = data as { ranked_item_ids?: string[] | null; item_ids?: string[] | null } | null;
  const rankedIds = (row?.ranked_item_ids ?? row?.item_ids ?? []).filter(Boolean);
  if (rankedIds.length === 0) return { updated: 0 };

  const [{ data: existingRows, error: existingErr }, { data: itemRows, error: itemErr }] =
    await Promise.all([
      sb.from('planning_priorities').select('id, name').in('id', rankedIds),
      sb.from('items').select('id, title').in('id', rankedIds),
    ]);
  if (existingErr) throw existingErr;
  if (itemErr) throw itemErr;

  const existingNameById = new Map(
    (existingRows ?? []).map((r: { id: string; name: string }) => [r.id, r.name]),
  );
  const itemTitleById = new Map(
    (itemRows ?? []).map((r: { id: string; title: string }) => [r.id, r.title]),
  );

  const upsertRows = rankedIds.map((id, i) => ({
    id,
    project_id: projectId,
    name: existingNameById.get(id) ?? itemTitleById.get(id) ?? 'Untitled priority',
    force_rank: i + 1,
  }));
  const { error: upsertErr } = await sb
    .from('planning_priorities')
    .upsert(upsertRows, { onConflict: 'id' });
  if (upsertErr) throw upsertErr;
  return { updated: upsertRows.length };
}

/**
 * Upsert planning priorities from live Prioritization items and materialize
 * hard/gate dependencies + prerequisites into planning_priority_dependencies.
 * Priority ids match items.id so the engine and item graph stay aligned (§4.7).
 */
export async function syncPlanningPrioritiesFromLiveItems(
  projectId: string,
  scenarioId: string | null,
  items: Item[],
  edges: ItemRelationshipEdge[],
): Promise<{ prioritiesCount: number; dependenciesCount: number }> {
  const sb = getSupabaseClient();
  const { priorities, priorityDependencies } = buildPlanningSyncFromLiveItems(
    items,
    edges,
    projectId,
    scenarioId,
    getItemEffortBandHours(),
  );

  if (priorities.length > 0) {
    // Preserve engine-only overrides already saved on planning_priorities so a
    // live-item sync does not wipe force-rank / resource type / WU estimates.
    const existingOverrides = await getPlanningOverrides(projectId);
    const rows = priorities.map((p) => {
      const ov = existingOverrides.get(p.id);
      const merged = ov
        ? {
            ...p,
            forceRank: ov.forceRank ?? p.forceRank,
            aboveTheLine: ov.aboveTheLine ?? p.aboveTheLine,
            valueScore: ov.valueScore ?? p.valueScore,
            effortScore: ov.effortScore ?? p.effortScore,
            priorityLevelWuEstimate: ov.priorityLevelWuEstimate ?? p.priorityLevelWuEstimate,
            priorityResourceType: ov.priorityResourceType ?? p.priorityResourceType,
            notes: ov.notes ?? p.notes,
          }
        : p;
      return {
        id: merged.id,
        project_id: projectId,
        name: merged.name,
        ...priorityToDb(merged),
      };
    });
    const { error: upsertError } = await sb.from('planning_priorities').upsert(rows, { onConflict: 'id' });
    if (upsertError) throw upsertError;
  }

  const { error: deleteDepsError } = await sb
    .from('planning_priority_dependencies')
    .delete()
    .eq('project_id', projectId)
    .like('notes', `${PLANNING_SYNC_DEP_NOTE_PREFIX}%`);
  if (deleteDepsError) throw deleteDepsError;

  if (priorityDependencies.length > 0) {
    const depRows = priorityDependencies.map((d) => ({
      id: d.id,
      project_id: projectId,
      from_priority_id: d.fromPriorityId,
      to_priority_id: d.toPriorityId,
      dependency_type: d.dependencyType,
      blocking: d.blocking,
      notes: d.notes,
    }));
    const { error: insertDepsError } = await sb.from('planning_priority_dependencies').insert(depRows);
    if (insertDepsError) throw insertDepsError;
  }

  return {
    prioritiesCount: priorities.length,
    dependenciesCount: priorityDependencies.length,
  };
}

/**
 * Upsert work units from live items (1:1 placeholder per active item) and
 * materialize work-unit-level dependencies from relationship edges.
 */
export async function syncPlanningWorkUnitsFromLiveItems(
  projectId: string,
  items: Item[],
  edges: ItemRelationshipEdge[],
): Promise<{ workUnitsCount: number; dependenciesCount: number }> {
  const sb = getSupabaseClient();
  const { workUnits, dependencies } = buildWorkUnitSyncFromLiveItems(
    items,
    edges,
    projectId,
    getItemEffortBandHours(),
  );

  if (workUnits.length > 0) {
    const { data: existingRows, error: existingErr } = await sb
      .from('planning_work_units')
      .select('id, notes')
      .eq('project_id', projectId);
    if (existingErr) throw existingErr;
    const existingNotesById = new Map(
      (existingRows ?? []).map((row: { id: string; notes: string | null }) => [row.id, row.notes]),
    );

    // Recipe resolution for the engine lane (Canon Doc 21 §4.2; mig 685 bridge).
    // itemToWorkUnit sets workUnit.id = item.id, so the work unit's recipe is the
    // one resolved from its source item's work_type. Resolution is a deterministic
    // registry lookup (workspace shadow row wins over the global built-in) — never
    // a judgement call. An item with no work_type leaves resolved_recipe_id null,
    // which is the recipe_pending state.
    //
    // The multiplier is applied at demand-math time, never baked in here.
    const itemById = new Map(items.map((item) => [item.id, item]));
    const recipeIdByKey = new Map<string, string | null>();
    for (const item of items) {
      if (!item.work_type) continue;
      const workspaceId = item.workspace_id ?? null;
      const key = `${workspaceId ?? ''}::${item.work_type}`;
      if (recipeIdByKey.has(key)) continue;
      recipeIdByKey.set(key, await resolveRecipeIdForWorkType(item.work_type, workspaceId));
    }

    const resolvedRecipeIdFor = (workUnitId: string): string | null => {
      const item = itemById.get(workUnitId);
      if (!item?.work_type) return null;
      return recipeIdByKey.get(`${item.workspace_id ?? ''}::${item.work_type}`) ?? null;
    };

    const rows = workUnits
      .filter((wu) => {
        const existingNotes = existingNotesById.get(wu.id);
        if (existingNotes === undefined) return true;
        return isSyncedWorkUnitNotes(existingNotes);
      })
      .map((wu) => ({
        id: wu.id,
        project_id: projectId,
        title: wu.title,
        ...workUnitToDb(wu),
        resolved_recipe_id: resolvedRecipeIdFor(wu.id),
      }));

    if (rows.length > 0) {
      const { error: upsertError } = await sb.from('planning_work_units').upsert(rows, { onConflict: 'id' });
      if (upsertError) throw upsertError;
    }
  }

  const { error: deleteDepsError } = await sb
    .from('planning_dependencies')
    .delete()
    .eq('project_id', projectId)
    .like('notes', `${PLANNING_SYNC_DEP_NOTE_PREFIX}%`);
  if (deleteDepsError) throw deleteDepsError;

  if (dependencies.length > 0) {
    const depRows = dependencies.map((d) => ({
      id: d.id,
      project_id: projectId,
      from_work_unit_id: d.fromWorkUnitId,
      to_work_unit_id: d.toWorkUnitId,
      dependency_type: d.dependencyType,
      blocking: d.blocking,
      notes: d.notes,
    }));
    const { error: insertDepsError } = await sb.from('planning_dependencies').insert(depRows);
    if (insertDepsError) throw insertDepsError;
  }

  return {
    workUnitsCount: workUnits.length,
    dependenciesCount: dependencies.length,
  };
}

/** Full §4.7 sync: priorities, priority deps, work units, and work-unit deps. */
export async function syncPlanningFromLiveItems(
  projectId: string,
  scenarioId: string | null,
  items: Item[],
  edges: ItemRelationshipEdge[],
): Promise<{
  prioritiesCount: number;
  priorityDependenciesCount: number;
  workUnitsCount: number;
  workUnitDependenciesCount: number;
}> {
  const priorityResult = await syncPlanningPrioritiesFromLiveItems(projectId, scenarioId, items, edges);
  const workUnitResult = await syncPlanningWorkUnitsFromLiveItems(projectId, items, edges);
  return {
    prioritiesCount: priorityResult.prioritiesCount,
    priorityDependenciesCount: priorityResult.dependenciesCount,
    workUnitsCount: workUnitResult.workUnitsCount,
    workUnitDependenciesCount: workUnitResult.dependenciesCount,
  };
}

// ---------------------------------------------------------------------------
// Engine input source resolution
// ---------------------------------------------------------------------------

export type EngineInputSource =
  | { type: 'live' }
  | { type: 'item_pool'; id: string; name: string }
  | { type: 'collection'; id: string; name: string }
  | { type: 'published_priority_list'; id: string; name: string }
  | { type: 'queue'; id: string; name: string }
  /** Hard container: only planning_priorities rows for this scenario. */
  | { type: 'scenario_portfolio'; id: string; name: string };

/**
 * Resolve the set of item IDs that belong to a given engine input source.
 * Returns null for 'live' (caller should use all items).
 */
export async function resolveItemIdsFromSource(
  source: EngineInputSource,
): Promise<{ ids: string[] | null; error: string | null }> {
  if (source.type === 'live') return { ids: null, error: null };

  try {
    const supabase = getSupabaseClient();

    if (source.type === 'item_pool') {
      // Canonical membership is item_pools.priority_ids (same as /prioritization/lists).
      // Reconcile orphans in item_pool_memberships before returning so engine row
      // counts match the pool UI.
      const { ids, error } = await resolveCanonicalItemPoolMemberIds(source.id);
      if (error) return { ids: null, error };
      console.debug('[resolveItemIdsFromSource] item_pool', {
        poolId: source.id,
        memberCount: ids.length,
        sampleIds: ids.slice(0, 3),
      });
      return { ids, error: null };
    }

    if (source.type === 'collection') {
      const { data, error } = await supabase
        .from('collection_memberships')
        .select('item_id')
        .eq('collection_id', source.id);
      if (error) return { ids: null, error: error.message };
      const ids = (data ?? []).map((r: { item_id: string }) => r.item_id).filter(Boolean);
      return { ids, error: null };
    }

    if (source.type === 'published_priority_list') {
      const { data, error } = await supabase
        .from('priority_sets')
        .select('ranked_item_ids, item_ids')
        .eq('id', source.id)
        .maybeSingle();
      if (error) return { ids: null, error: error.message };
      const row = data as { ranked_item_ids?: string[]; item_ids?: string[] } | null;
      const ids = (row?.ranked_item_ids ?? row?.item_ids ?? []).filter(Boolean);
      return { ids, error: null };
    }

    if (source.type === 'queue') {
      const { data, error } = await supabase
        .from('items')
        .select('id')
        .eq('queue_id', source.id);
      if (error) return { ids: null, error: error.message };
      const ids = (data ?? []).map((r: { id: string }) => r.id).filter(Boolean);
      return { ids, error: null };
    }

    if (source.type === 'scenario_portfolio') {
      const { data, error } = await supabase
        .from('planning_priorities')
        .select('id')
        .eq('scenario_id', source.id);
      if (error) return { ids: null, error: error.message };
      const ids = (data ?? []).map((r: { id: string }) => r.id).filter(Boolean);
      return { ids, error: null };
    }

    return { ids: null, error: 'Unknown source type' };
  } catch (e) {
    return { ids: null, error: e instanceof Error ? e.message : 'Failed to resolve source' };
  }
}

// ---------------------------------------------------------------------------
// Scope eligibility — effort vs attention (PRD Part C §6.1)
// ---------------------------------------------------------------------------

/**
 * Is this Engine Input Scope something the planning engine can read?
 *
 * The engine reads effort. A list that ranks people to develop or risks to watch
 * is a perfectly good ranked list that this engine has nothing to say about, and
 * binding one should produce an explanation rather than a zero-capacity run that
 * reads as a failure of the plan.
 *
 * Measurement runs against the live items already in memory — no extra fetch —
 * except for `scenario_portfolio`, whose ids are planning_priorities rows rather
 * than items, so counting them against `items` would report a false zero.
 */
export async function resolveScopePlannability(args: {
  engineInput: EngineInputSource;
  items: Item[];
  /** null = all live items. */
  scopedItemIds: Set<string> | null;
}): Promise<ScopePlannability> {
  const { engineInput, items, scopedItemIds } = args;

  if (engineInput.type === 'scenario_portfolio') {
    return resolvePlannability({
      explicitFlag: null,
      requiredItemCategory: null,
      effortBearingCount: 0,
      itemCount: 0,
    });
  }

  let explicitFlag: boolean | null = null;
  let requiredItemCategory: string | null = null;

  if (engineInput.type === 'item_pool') {
    // Only lists carry the flag today; every other scope kind falls through to
    // measurement, which is the honest answer when nobody has declared one.
    const { data, error } = await getSupabaseClient()
      .from('item_pools')
      .select('plannable, required_item_category')
      .eq('id', engineInput.id)
      .maybeSingle();
    if (error) {
      console.warn('[resolveScopePlannability] could not read list eligibility', error.message);
    } else if (data) {
      const row = data as { plannable: boolean | null; required_item_category: string | null };
      explicitFlag = row.plannable ?? null;
      requiredItemCategory = row.required_item_category ?? null;
    }
  }

  const inScope = scopedItemIds == null ? items : items.filter((i) => scopedItemIds.has(i.id));
  const bandHours = getItemEffortBandHours();
  const effortBearingCount = inScope.filter(
    (item) => (resolveItemEffortCapacityUnits(item, bandHours).capacityUnits ?? 0) > 0,
  ).length;

  return resolvePlannability({
    explicitFlag,
    requiredItemCategory,
    effortBearingCount,
    itemCount: inScope.length,
  });
}

/** Persist the owner's explicit effort-vs-attention decision for a list. */
export async function setListPlannable(
  itemPoolId: string,
  plannable: boolean | null,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('item_pools')
    .update({ plannable })
    .eq('id', itemPoolId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Work Units
// ---------------------------------------------------------------------------

export async function getWorkUnits(projectId: string): Promise<WorkUnit[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_work_units')
    .select('*')
    .eq('project_id', projectId)
    .order('constraint_adjusted_rank', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []).map(toWorkUnit);
}

export async function createWorkUnit(projectId: string, data: Partial<WorkUnit>): Promise<WorkUnit> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_work_units')
    .insert({ project_id: projectId, title: data.title ?? 'New Work Unit', ...workUnitToDb(data) })
    .select()
    .single();
  if (error) throw error;
  return toWorkUnit(row);
}

export async function updateWorkUnit(id: string, data: Partial<WorkUnit>): Promise<WorkUnit> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_work_units')
    .update(workUnitToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  markPlanningEngineStale();
  return toWorkUnit(row);
}

export async function deleteWorkUnit(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('planning_work_units').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Work-unit Dependencies
// ---------------------------------------------------------------------------

export async function getDependencies(projectId: string): Promise<Dependency[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_dependencies')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toDependency);
}

export async function createDependency(projectId: string, data: Partial<Dependency>): Promise<Dependency> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_dependencies')
    .insert({ project_id: projectId, ...dependencyToDb(data) })
    .select()
    .single();
  if (error) throw error;
  return toDependency(row);
}

export async function updateDependency(id: string, data: Partial<Dependency>): Promise<Dependency> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_dependencies')
    .update(dependencyToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return toDependency(row);
}

export async function deleteDependency(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('planning_dependencies').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Priority Dependencies (v21.1)
// ---------------------------------------------------------------------------

export async function getPriorityDependencies(projectId: string): Promise<PriorityDependency[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_priority_dependencies')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toPriorityDependency);
}

export async function createPriorityDependency(
  projectId: string,
  data: Partial<PriorityDependency>,
): Promise<PriorityDependency> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_priority_dependencies')
    .insert({ project_id: projectId, ...priorityDependencyToDb(data) })
    .select()
    .single();
  if (error) throw error;
  return toPriorityDependency(row);
}

export async function updatePriorityDependency(
  id: string,
  data: Partial<PriorityDependency>,
): Promise<PriorityDependency> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_priority_dependencies')
    .update(priorityDependencyToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return toPriorityDependency(row);
}

export async function deletePriorityDependency(id: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_priority_dependencies')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Work-unit Capacity Rows
// ---------------------------------------------------------------------------

export async function getCapacityRows(
  projectId: string,
  scenarioId?: string,
): Promise<CapacityModelRow[]> {
  const sb = getSupabaseClient();
  let query = sb
    .from('planning_capacity')
    .select('*, planning_scenarios(*)')
    .eq('project_id', projectId);
  if (scenarioId) query = query.eq('scenario_id', scenarioId);

  const { data, error } = await query.order('created_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
    const scenario = scenarioRow
      ? toScenario(scenarioRow)
      : fallbackScenario(row.scenario_id as string, row.project_id as string);
    return toCapacityRow(row, scenario);
  });
}

/**
 * Pools the engine should actually plan against for this scenario.
 *
 * Manual mode returns exactly what it returns today. Roster mode adds the
 * per-type roll-up of active role assignments *alongside* the manual rows —
 * roster mode adds supply, it never hides what someone typed in.
 *
 * The roster read degrades to manual pools if the roster is unreachable, so a
 * roster-mode scenario never loses the ability to run.
 */
export async function getEffectiveCapacityRows(
  scenario: Scenario,
): Promise<{ rows: CapacityModelRow[]; derived: DerivedRosterPool[] }> {
  const stored = await getCapacityRows(scenario.projectId, scenario.id);
  if (!scenarioUsesRoster(scenario)) return { rows: stored, derived: [] };

  const { resources, assignments } = await getRosterSupply(scenario.projectId);
  const derived = deriveRosterCapacityRows({ scenario, resources, assignments });
  return { rows: mergeCapacityRows(stored, derived), derived };
}

/** Priority-grain equivalent of getEffectiveCapacityRows. */
export async function getEffectivePriorityCapacityRows(
  scenario: Scenario,
): Promise<{ rows: PriorityCapacityRow[]; derived: DerivedRosterPool[] }> {
  const stored = await getPriorityCapacityRows(scenario.projectId, scenario.id);
  if (!scenarioUsesRoster(scenario)) return { rows: stored, derived: [] };

  const { resources, assignments } = await getRosterSupply(scenario.projectId);
  const derived = deriveRosterCapacityRows({ scenario, resources, assignments });
  // A DerivedRosterPool is structurally a CapacityModelRow, and PriorityCapacityRow
  // is the same shape — the engine reads only resourceType and workUnitCapacity
  // off either. Priority-estimate scenarios in roster mode would otherwise see
  // no roster supply at all, which is the mode the demo actually runs in.
  return { rows: [...stored, ...derived], derived };
}

export async function createCapacityRow(
  projectId: string,
  data: Partial<CapacityModelRow>,
): Promise<CapacityModelRow> {
  const sb = getSupabaseClient();
  const { data: row, error } = await sb
    .from('planning_capacity')
    .insert({ project_id: projectId, ...capacityRowToDb(data) })
    .select('*, planning_scenarios(*)')
    .single();
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
  const scenario = scenarioRow
    ? toScenario(scenarioRow)
    : fallbackScenario(row.scenario_id, row.project_id);
  markPlanningEngineStale();
  return toCapacityRow(row, scenario);
}

export async function updateCapacityRow(
  id: string,
  data: Partial<CapacityModelRow>,
): Promise<CapacityModelRow> {
  const sb = getSupabaseClient();
  const { data: row, error } = await sb
    .from('planning_capacity')
    .update(capacityRowToDb(data))
    .eq('id', id)
    .select('*, planning_scenarios(*)')
    .single();
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
  const scenario = scenarioRow
    ? toScenario(scenarioRow)
    : fallbackScenario(row.scenario_id, row.project_id);
  markPlanningEngineStale();
  return toCapacityRow(row, scenario);
}

export async function deleteCapacityRow(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('planning_capacity').delete().eq('id', id);
  if (error) throw error;
  markPlanningEngineStale();
}

// ---------------------------------------------------------------------------
// Priority-Level Capacity Rows (v21.1)
// ---------------------------------------------------------------------------

export async function getPriorityCapacityRows(
  projectId: string,
  scenarioId?: string,
): Promise<PriorityCapacityRow[]> {
  const sb = getSupabaseClient();
  let query = sb
    .from('planning_priority_capacity')
    .select('*, planning_scenarios(*)')
    .eq('project_id', projectId);
  if (scenarioId) query = query.eq('scenario_id', scenarioId);

  const { data, error } = await query.order('created_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
    const scenario = scenarioRow
      ? toScenario(scenarioRow)
      : fallbackScenario(row.scenario_id as string, row.project_id as string);
    return toPriorityCapacityRow(row, scenario);
  });
}

export async function createPriorityCapacityRow(
  projectId: string,
  data: Partial<PriorityCapacityRow>,
): Promise<PriorityCapacityRow> {
  const sb = getSupabaseClient();
  const { data: row, error } = await sb
    .from('planning_priority_capacity')
    .insert({ project_id: projectId, ...capacityRowToDb(data) })
    .select('*, planning_scenarios(*)')
    .single();
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
  const scenario = scenarioRow
    ? toScenario(scenarioRow)
    : fallbackScenario(row.scenario_id, row.project_id);
  markPlanningEngineStale();
  return toPriorityCapacityRow(row, scenario);
}

export async function updatePriorityCapacityRow(
  id: string,
  data: Partial<PriorityCapacityRow>,
): Promise<PriorityCapacityRow> {
  const sb = getSupabaseClient();
  const { data: row, error } = await sb
    .from('planning_priority_capacity')
    .update(capacityRowToDb(data))
    .eq('id', id)
    .select('*, planning_scenarios(*)')
    .single();
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scenarioRow = (row as any).planning_scenarios as Record<string, unknown> | null;
  const scenario = scenarioRow
    ? toScenario(scenarioRow)
    : fallbackScenario(row.scenario_id, row.project_id);
  markPlanningEngineStale();
  return toPriorityCapacityRow(row, scenario);
}

export async function deletePriorityCapacityRow(id: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_priority_capacity')
    .delete()
    .eq('id', id);
  if (error) throw error;
  markPlanningEngineStale();
}

// ---------------------------------------------------------------------------
// Team Participation Requirements (v22)
// ---------------------------------------------------------------------------

export async function getTeamParticipation(
  projectId: string,
  options?: { priorityId?: string; workUnitId?: string },
): Promise<TeamParticipation[]> {
  let query = getSupabaseClient()
    .from('planning_team_participation')
    .select('*')
    .eq('project_id', projectId);
  if (options?.priorityId) query = query.eq('priority_id', options.priorityId);
  if (options?.workUnitId) query = query.eq('work_unit_id', options.workUnitId);

  const { data, error } = await query.order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toTeamParticipation);
}

export async function createTeamParticipation(
  projectId: string,
  data: Partial<TeamParticipation>,
): Promise<TeamParticipation> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_team_participation')
    .insert({
      project_id: projectId,
      required_team: data.requiredTeam ?? 'Unknown Team',
      ...teamParticipationToDb(data),
    })
    .select()
    .single();
  if (error) throw error;
  return toTeamParticipation(row);
}

export async function updateTeamParticipation(
  id: string,
  data: Partial<TeamParticipation>,
): Promise<TeamParticipation> {
  const { data: row, error } = await getSupabaseClient()
    .from('planning_team_participation')
    .update(teamParticipationToDb(data))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return toTeamParticipation(row);
}

export async function deleteTeamParticipation(id: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_team_participation')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Engine → Governance bridge (Doc 104 Steps 7→8 / critical-1)
// ---------------------------------------------------------------------------

export interface CommitEngineResult {
  planVersionId: string | null;
  /** Set when nothing was committed (the server transaction refused or failed). */
  error: string | null;
  /** Number of plan_record rows persisted with the version. */
  recordCount: number;
  /** Governance initiatives (+ one work unit each) created for Included priorities. */
  initiativesCreated: number;
  /**
   * Included priorities with no item in this workspace (scenario-grain
   * priorities). They are recorded in the payload and plan_records but have no
   * initiative — reported, not silently dropped.
   */
  skippedPriorityIds: string[];
}

/** Must match the plan_version_change_reason Postgres enum exactly. */
export type PlanVersionChangeReason =
  | 'constraint_shift'
  | 'assumption_break'
  | 'scope_adjustment'
  | 'other';

/** Plan version payload for an engine commit (Doc 107 §4.1). Pure. */
export function buildEnginePlanVersionPayload(output: PlanningEngineOutput): Record<string, unknown> {
  return {
    mode: output.planningMode,
    scenario_id: output.scenario.id,
    scenario_name: output.scenario.name,
    generated_at: output.generatedAt,
    plan_records: output.planRecords.map((r) => ({
      work_unit_id: r.workUnit.id,
      work_unit_title: r.workUnit.title,
      priority_id: r.priority?.id ?? null,
      priority_name: r.priority?.name ?? null,
      decision: r.planDecision,
      decision_with_participation: r.decisionWithParticipation,
      decision_reason: r.decisionReason,
      work_units_required: r.workUnitsRequired,
      running_demand_before: r.runningDemandBefore,
      running_demand_after: r.runningDemandAfter,
      dependency_status: r.dependencyStatus,
      readiness_status: r.readinessStatus,
      capacity_status: r.capacityStatus,
      participation_requirement_count: r.participationRequirementCount,
      participation_capacity_status: r.participationCapacityStatus,
      capability_status: r.capabilityStatus,
      commitment_status: r.commitmentStatus,
      participation_risk: r.participationRisk,
    })),
    priority_plan_records: output.priorityPlanRecords.map((r) => ({
      priority_id: r.priority.id,
      priority_name: r.priority.name,
      decision: r.planDecision,
      decision_with_participation: r.decisionWithParticipation,
      decision_reason: r.decisionReason,
      achievability: r.consistencyCheck.status === 'OK' ? 'Fully Achievable' : 'Partially Achievable',
      identified_work_units_total: r.identifiedWorkUnitsTotal,
      item_planning_demand: r.itemPlanningDemand,
      active_mode_planning_demand: r.activeModeplanningDemand,
      running_demand_before: r.runningDemandBefore,
      running_demand_after: r.runningDemandAfter,
      participation_requirement_count: r.participationRequirementCount,
      participation_capacity_status: r.participationCapacityStatus,
      capability_status: r.capabilityStatus,
      commitment_status: r.commitmentStatus,
      participation_risk: r.participationRisk,
    })),
    resource_summary: output.resourceSummaries.map((s) => ({
      resource_type: s.resourceType,
      capacity_wu: s.capacityWU,
      demand_wu: s.demandWU,
      remaining_wu: s.remainingWU,
      utilization_percent: s.utilizationPercent,
    })),
    executive_summary: {
      total_work_units: output.executiveSummary.totalWorkUnits,
      included_count: output.executiveSummary.includedCount,
      deferred_count: output.executiveSummary.deferredCount,
      blocked_count: output.executiveSummary.blockedCount,
      not_ready_count: output.executiveSummary.notReadyCount,
      no_capacity_model_count: output.executiveSummary.noCapacityModelCount,
      overall_feasibility: output.executiveSummary.overallFeasibility,
      participation_at_risk_count: output.executiveSummary.participationAtRiskCount,
      participation_blocked_count: output.executiveSummary.participationBlockedCount,
    },
  };
}

/**
 * plan_records rows (Doc 106 canonical query surfaces) for an engine commit —
 * without plan_version_id; the server stamps it inside the same transaction
 * that creates the version. Pure.
 */
export function buildEnginePlanRecordRows(output: PlanningEngineOutput): Array<Record<string, unknown>> {
  const workUnitRows = output.planRecords.map((r: PlanRecord, idx: number) => ({
    grain: 'work_unit',
    planning_priority_id: r.priority?.id ?? null,
    planning_work_unit_id: r.workUnit.id,
    decision: r.planDecision,
    decision_with_participation: r.decisionWithParticipation,
    decision_reason: r.decisionReason,
    work_units_required: r.workUnitsRequired,
    running_demand_before: r.runningDemandBefore,
    running_demand_after: r.runningDemandAfter,
    readiness_status: r.readinessStatus,
    dependency_status: r.dependencyStatus,
    capacity_status: r.capacityStatus,
    participation_requirement_count: r.participationRequirementCount,
    participation_capacity_status: r.participationCapacityStatus,
    capability_status_value: r.capabilityStatus,
    commitment_status: r.commitmentStatus,
    participation_risk: r.participationRisk,
    work_unit_sequence: idx,
    priority_rank: r.priority?.forceRank ?? null,
    priority_name: r.priority?.name ?? null,
    work_unit_title: r.workUnit.title,
  }));

  const priorityRows = output.priorityPlanRecords.map((r: PriorityPlanRecord) => ({
    grain: 'priority',
    planning_priority_id: r.priority.id,
    planning_work_unit_id: null,
    decision: r.planDecision,
    decision_with_participation: r.decisionWithParticipation,
    decision_reason: r.decisionReason,
    achievability: r.consistencyCheck.status === 'OK' ? r.decisionWithParticipation : 'Partially Achievable',
    work_units_required: r.identifiedWorkUnitsTotal,
    running_demand_before: r.runningDemandBefore,
    running_demand_after: r.runningDemandAfter,
    participation_requirement_count: r.participationRequirementCount,
    participation_capacity_status: r.participationCapacityStatus,
    capability_status_value: r.capabilityStatus,
    commitment_status: r.commitmentStatus,
    participation_risk: r.participationRisk,
    priority_rank: r.priority.forceRank ?? null,
    priority_name: r.priority.name,
    work_unit_title: null,
  }));

  return [...workUnitRows, ...priorityRows];
}

/**
 * commitEngineToPlanVersion — Doc 104 Steps 7→8
 *
 * Bridges the Engine Module and Governance Layer through ONE server
 * transaction (commit_engine_plan_version, migration 764): the immutable
 * plan_version, every plan_record (Q-PLAN-004…008 surfaces), and the
 * governance initiatives + work units the Doc 15 POR gates read — all
 * written, or none. The creator is the signed-in person (auth.uid()).
 */
export async function commitEngineToPlanVersion(params: {
  planCycleId: string;
  output: PlanningEngineOutput;
  changeReason?: PlanVersionChangeReason;
  engineVersion?: string;
}): Promise<CommitEngineResult> {
  const { planCycleId, output, changeReason, engineVersion = 'module-v22' } = params;
  const failed = (error: string): CommitEngineResult => ({
    planVersionId: null, error, recordCount: 0, initiativesCreated: 0, skippedPriorityIds: [],
  });
  try {
    const { data, error } = await getSupabaseClient().rpc('commit_engine_plan_version', {
      p_plan_cycle_id: planCycleId,
      p_payload: buildEnginePlanVersionPayload(output),
      p_plan_records: buildEnginePlanRecordRows(output),
      p_change_reason: changeReason ?? 'other',
      p_engine_version: engineVersion,
    });
    if (error) return failed(error.message);
    const res = (data ?? {}) as {
      success?: boolean;
      error?: string;
      plan_version_id?: string;
      record_count?: number;
      initiatives_created?: number;
      skipped_priority_ids?: string[];
    };
    if (!res.success || !res.plan_version_id) {
      return failed(res.error ?? 'The plan version was not created');
    }
    return {
      planVersionId: res.plan_version_id,
      error: null,
      recordCount: res.record_count ?? 0,
      initiativesCreated: res.initiatives_created ?? 0,
      skippedPriorityIds: res.skipped_priority_ids ?? [],
    };
  } catch (e) {
    return failed(e instanceof Error ? e.message : 'The plan version was not created');
  }
}

/**
 * backfillGovernanceRecordsForPlanVersion — complete the governance-layer rows
 * (initiatives + work_units) an engine-committed plan version needs to satisfy
 * the Doc 15 POR gates (BucketsMaterialized reads initiatives → work_units;
 * bucket allocations derive from them).
 *
 * HEAL PATH ONLY (Run engine on a version committed before synthesis existed,
 * or one whose commit partially failed). New commits get these rows inside
 * commit_engine_plan_version's transaction.
 *
 * This used to be a second implementation of the same rule, written from the
 * client — and its own comment claimed it produced rows identical to the commit
 * path's, which two implementations cannot guarantee and did not: both filtered
 * to priority-grain plan records, so a version committed from a Work Units
 * scenario got no governance rows from either. Migration 777 moved the rule into
 * internal_plan_version_governance() and exposed it as heal_plan_version_governance;
 * there is now one implementation and the claim is structural.
 */
export async function backfillGovernanceRecordsForPlanVersion(
  planVersionId: string,
): Promise<{ created: number; error: string | null; failures: string[] }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('heal_plan_version_governance', {
      p_plan_version_id: planVersionId,
    } as never);
    if (error) return { created: 0, error: error.message, failures: [] };

    const res = (data ?? {}) as {
      success?: boolean;
      error?: string;
      initiatives_created?: number;
      work_units_created?: number;
      below_the_line_count?: number;
      skipped_priority_ids?: string[];
    };
    if (!res.success) {
      return { created: 0, error: res.error ?? 'Governance records were not completed', failures: [] };
    }
    // Priorities the plan named but this workspace has no item for: reported,
    // never silently dropped (initiatives.item_id is the commitment identity).
    const failures = (res.skipped_priority_ids ?? []).map(
      (id) => `${id}: no item in this workspace, so it cannot carry a commitment`,
    );
    return { created: res.initiatives_created ?? 0, error: null, failures };
  } catch (e) {
    return { created: 0, error: e instanceof Error ? e.message : 'Governance record backfill failed', failures: [] };
  }
}

// ---------------------------------------------------------------------------
// Feasibility check (C-mode) — respects active scenario × input scope
// ---------------------------------------------------------------------------

const PLANNING_ENGINE_STALE_KEY = 'planning-engine-stale';

/** Inputs changed (capacity, work units, etc.) — feasibility should bypass cached runs. */
export function markPlanningEngineStale(): void {
  try {
    sessionStorage.setItem(PLANNING_ENGINE_STALE_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
}

function isPlanningEngineStale(): boolean {
  try {
    return sessionStorage.getItem(PLANNING_ENGINE_STALE_KEY) != null;
  } catch {
    return false;
  }
}

function clearPlanningEngineStale(): void {
  try {
    sessionStorage.removeItem(PLANNING_ENGINE_STALE_KEY);
  } catch {
    /* noop */
  }
}

/** Same session key as PlanningEngineContext. */
export function readPersistedEngineInput(): EngineInputSource {
  try {
    const stored = sessionStorage.getItem('planning-engine-input');
    if (stored) return JSON.parse(stored) as EngineInputSource;
  } catch {
    /* noop */
  }
  return { type: 'live' };
}

/** Resolve scoped item IDs for an engine input (mirrors PlanningEngineContext). */
export async function resolveEngineScopeSet(
  engineInput: EngineInputSource,
): Promise<Set<string> | null> {
  if (engineInput.type === 'live') return null;

  const { ids } = await resolveItemIdsFromSource(engineInput);
  if (ids && ids.length > 0) return new Set(ids);

  if (engineInput.type === 'item_pool') {
    const localPool = useItemPoolsStore.getState().itemPools.find((p) => p.id === engineInput.id);
    if (localPool && localPool.priorityIds.length > 0) {
      return new Set(localPool.priorityIds);
    }
  }

  return ids !== null ? new Set(ids) : null;
}

/**
 * Run feasibility for the active scenario using the same scope + capacity as the
 * Planning Engine workbench. Prefers the last saved planning_runs instance unless
 * forceRerun is set (manual refresh after capacity edits).
 */
export async function runFeasibilityCheck(args: {
  projectId: string;
  scenario: Scenario;
  engineInput: EngineInputSource;
  items: Item[];
  edges: ItemRelationshipEdge[];
  forceRerun?: boolean;
}): Promise<PlanningEngineOutput> {
  const { projectId, scenario, engineInput, items, edges, forceRerun } = args;
  const mustRerun = forceRerun || isPlanningEngineStale();

  if (!mustRerun) {
    const saved = await getPlanningRun(projectId, scenario.id, engineInput);
    if (saved?.output) return saved.output;
  }

  const scopedItemIds = await resolveEngineScopeSet(engineInput);

  const output = await runEngineScoped({
    projectId,
    scenario,
    items,
    edges,
    scopedItemIds,
    scopeKind: engineInput.type,
  });

  await upsertPlanningRun({
    projectId,
    scenarioId: scenario.id,
    engineInput,
    scopedItemIds: scopedItemIds ? Array.from(scopedItemIds) : null,
    output,
  });
  clearPlanningEngineStale();

  return output;
}

// ---------------------------------------------------------------------------
// Run engine
// ---------------------------------------------------------------------------

/**
 * Run the planning engine against a pre-built input (e.g. from the live-items adapter).
 * Bypasses all DB fetches — useful for ad-hoc planning without a formal scenario.
 */
export function runEngineWithInput(input: import('./planningEngineModule.types').PlanningEngineInput): PlanningEngineOutput {
  return runPlanningEngineWithBands(input);
}

export async function runEngine(
  projectId: string,
  scenarioId: string,
): Promise<PlanningEngineOutput> {
  const sb = getSupabaseClient();

  const [
    { data: scenarioRow, error: se },
    priorities,
    workUnits,
    dependencies,
    priorityDependencies,
  ] = await Promise.all([
    sb.from('planning_scenarios').select('*').eq('id', scenarioId).single(),
    getPriorities(projectId),
    getWorkUnits(projectId),
    getDependencies(projectId),
    getPriorityDependencies(projectId),
  ]);
  if (se) throw se;
  const scenario = toScenario(scenarioRow);

  const [effectiveCapacity, effectivePriorityCapacity, teamParticipation] = await Promise.all([
    getEffectiveCapacityRows(scenario),
    getEffectivePriorityCapacityRows(scenario),
    getTeamParticipation(projectId),
  ]);
  const capacityRows = effectiveCapacity.rows;
  const priorityCapacityRows = effectivePriorityCapacity.rows;
  const qualification = await buildQualificationInput({
    scenario,
    manualWorkUnitRows: capacityRows,
    manualPriorityRows: priorityCapacityRows,
  });

  return runPlanningEngineWithBands({
    scenario,
    priorities,
    workUnits,
    dependencies,
    priorityDependencies,
    capacityRows,
    priorityCapacityRows,
    teamParticipation,
    qualification: qualification ?? undefined,
  });
}

// ---------------------------------------------------------------------------
// Multi-instance runs (Scenario × Input Scope)
// ---------------------------------------------------------------------------

const LIVE_SCOPE_SENTINEL = '00000000-0000-0000-0000-000000000000';

export function engineInstanceKey(scenarioId: string, input: EngineInputSource): string {
  if (input.type === 'live') return `${scenarioId}|live`;
  return `${scenarioId}|${input.type}|${input.id}`;
}

export function engineInputKindLabel(type: EngineInputSource['type']): string {
  switch (type) {
    case 'live':
      return 'All Live Items';
    case 'item_pool':
      return 'Item Pool';
    case 'collection':
      return 'Collection';
    case 'published_priority_list':
      return 'Published List';
    case 'queue':
      return 'Queue';
    case 'scenario_portfolio':
      return 'Scenario priorities';
    default:
      return 'Scope';
  }
}

export function engineInputLabel(input: EngineInputSource): string {
  if (input.type === 'live') return 'All Live Items';
  return `${engineInputKindLabel(input.type)}: ${input.name}`;
}

export interface PlanningRunRecord {
  id: string;
  projectId: string;
  scenarioId: string;
  inputSourceType: EngineInputSource['type'];
  inputSourceId: string | null;
  inputSourceName: string | null;
  scopedItemIds: string[] | null;
  output: PlanningEngineOutput;
  executiveSummary: ExecutiveSummary;
  runStatus: string;
  runStartedAt: string;
  runCompletedAt: string | null;
  updatedAt: string;
}

function toPlanningRun(row: Record<string, unknown>): PlanningRunRecord {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    scenarioId: row.scenario_id as string,
    inputSourceType: row.input_source_type as EngineInputSource['type'],
    inputSourceId: (row.input_source_id as string | null) ?? null,
    inputSourceName: (row.input_source_name as string | null) ?? null,
    scopedItemIds: (row.scoped_item_ids as string[] | null) ?? null,
    // Runs saved before 2026-10-28 carry "Deferred - Capacity"; read them as "Out - capacity".
    output: readStoredEngineOutput(row.output as PlanningEngineOutput),
    executiveSummary: (row.executive_summary as ExecutiveSummary) ?? (row.output as PlanningEngineOutput).executiveSummary,
    runStatus: row.run_status as string,
    runStartedAt: row.run_started_at as string,
    runCompletedAt: (row.run_completed_at as string | null) ?? null,
    updatedAt: (row.updated_at as string) ?? (row.run_completed_at as string) ?? (row.run_started_at as string),
  };
}

/** Everything `buildEngineInputScoped` and `runEngineScoped` both need. */
export interface ScopedEngineRunArgs {
  projectId: string;
  scenario: Scenario;
  items: Item[];
  edges: ItemRelationshipEdge[];
  /** null = all live items; non-null Set = only those IDs (may be empty) */
  scopedItemIds: Set<string> | null;
  /** Input Scope kind that produced scopedItemIds — scenario_portfolio ids are priority rows, not items. */
  scopeKind?: EngineInputSource['type'];
  /**
   * Package / Competing groups, from the same relationships store `edges` comes
   * from. Optional: absent means the run enforces no group rules, which is the
   * behaviour every caller had before these were honoured.
   */
  relationshipGroups?: ItemRelationshipGroupLike[];
  relationshipGroupMemberships?: ItemRelationshipGroupMembershipLike[];
}

/**
 * Assemble the engine input for a scoped item set, without running anything.
 *
 * Split out of `runEngineScoped` so a caller that wants to do something OTHER
 * than one deterministic pass over this input — the Monte Carlo simulation
 * samples it a thousand times — plans against the identical input the engine
 * itself would have used, rather than a second, drifting assembly of it.
 *
 * Merges live items with persisted planning_priorities overrides and
 * planning_work_units edits so manual input changes affect feasibility.
 * Unbound scenario priorities (Keith/Sample seeds, Priorities-tab creates) are
 * included so Above/Below matches the Launch Calendar portfolio.
 */
export async function buildEngineInputScoped(
  args: ScopedEngineRunArgs,
): Promise<PlanningEngineInput> {
  const {
    projectId, scenario, items, edges, scopedItemIds, scopeKind,
    relationshipGroups, relationshipGroupMemberships,
  } = args;

  const filteredItems =
    scopedItemIds == null
      ? items
      : items.filter((item) => scopedItemIds.has(item.id));

  if (
    scopedItemIds != null &&
    filteredItems.length === 0 &&
    items.length > 0 &&
    scopeKind !== 'scenario_portfolio' // expected there: ids are priority rows, merged below
  ) {
    console.warn('[runEngineScoped] scope IDs did not match any live items', {
      scopeSize: scopedItemIds.size,
      liveItemCount: items.length,
      sampleScopeIds: Array.from(scopedItemIds).slice(0, 3),
      sampleItemIds: items.slice(0, 3).map((i) => i.id),
    });
  }

  const [
    effectiveCapacity,
    effectivePriorityCapacity,
    teamParticipation,
    priorityOverrides,
    dbWorkUnits,
    dbDependencies,
    dbPriorityDependencies,
    dbPriorities,
  ] = await Promise.all([
    // Roster mode rolls active role assignments into per-type pools here, so
    // the engine itself needs no change at all — derivation, not replacement.
    getEffectiveCapacityRows(scenario),
    getEffectivePriorityCapacityRows(scenario),
    getTeamParticipation(projectId),
    getPlanningOverrides(projectId),
    getWorkUnits(projectId),
    getDependencies(projectId),
    getPriorityDependencies(projectId),
    getPriorities(projectId),
  ]);
  const capacityRows = effectiveCapacity.rows;
  const priorityCapacityRows = effectivePriorityCapacity.rows;

  // Phase B. Null unless the scenario opted into the roster, in which case the
  // pool lookup narrows per item and a below-the-line answer can name the
  // qualification that failed rather than saying "no capacity".
  const qualification = await buildQualificationInput({
    scenario,
    manualWorkUnitRows: capacityRows,
    manualPriorityRows: priorityCapacityRows,
  });

  const input = buildScopedPlanningEngineInput({
    items,
    edges,
    scenario,
    projectId,
    scopedItemIds,
    scopeKind,
    dbWorkUnits,
    dbDependencies,
    dbPriorityDependencies,
    dbPriorities,
    priorityOverrides,
    capacityRows,
    priorityCapacityRows,
    teamParticipation,
    relationshipGroups,
    relationshipGroupMemberships,
    bandHours: getItemEffortBandHours(),
    acceptedRanks: await acceptedRanksForPlanCycle(scenario.planCycleId),
  });

  return {
    ...input,
    qualification: qualification ?? undefined,
    recipeMultipliers: await recipeMultipliersForScenario(scenario),
  };
}

/**
 * The ranking the organization accepted, as item id → 1-based rank.
 *
 * Read from the accepted Catchball packet for this scenario's plan cycle: its
 * `aggregated_rank_list` is the ordered item ids the room produced and the
 * executive accepted. Nothing carried this into planning before, so the engine
 * sorted by `forceRank ?? Infinity` then createdAt — pool order — and the agreed
 * ranking had no effect on the plan. On the Sightline cycle that put the room's
 * #1, #2 and #3 below the line.
 *
 * Returns an empty map when there is no accepted packet, which leaves every
 * priority exactly as it was.
 */
export async function acceptedRanksForPlanCycle(
  planCycleId: string | null | undefined,
): Promise<ReadonlyMap<string, number>> {
  const empty = new Map<string, number>();
  if (!planCycleId) return empty;
  const sb = getSupabaseClient();

  // A plan cycle does not reference the prioritization cycle directly — it
  // names the INPUT it was built from. For an item-pool input the chain is
  // pool → track → cycle, and the cycle's loops carry the packets.
  const { data: planCycle } = await sb
    .from('plan_cycles')
    .select('input_type, input_reference_id')
    .eq('id', planCycleId)
    .maybeSingle();
  const input = planCycle as { input_type?: string | null; input_reference_id?: string | null } | null;
  if (!input?.input_reference_id || input.input_type !== 'item_pool') return empty;

  const { data: pool } = await sb
    .from('item_pools')
    .select('track_id')
    .eq('id', input.input_reference_id)
    .maybeSingle();
  const trackId = (pool as { track_id?: string | null } | null)?.track_id;
  if (!trackId) return empty;

  const { data: track } = await sb
    .from('tracks')
    .select('cycle_id')
    .eq('id', trackId)
    .maybeSingle();
  const cycleId = (track as { cycle_id?: string | null } | null)?.cycle_id;
  if (!cycleId) return empty;

  const { data: loops } = await sb.from('catchball_loops').select('id').eq('cycle_id', cycleId);
  const loopIds = ((loops ?? []) as { id: string }[]).map((l) => l.id);
  if (loopIds.length === 0) return empty;

  const { data: packets } = await sb
    .from('catchball_proposals')
    .select('aggregated_rank_list, status, updated_at')
    .in('loop_id', loopIds)
    .in('status', ['accepted', 'accepted_provisional'])
    .order('updated_at', { ascending: false })
    .limit(1);

  const list = ((packets ?? [])[0] as { aggregated_rank_list?: string[] | null } | undefined)?.aggregated_rank_list;
  if (!Array.isArray(list) || list.length === 0) return empty;

  const ranks = new Map<string, number>();
  list.forEach((itemId, index) => {
    if (typeof itemId === 'string' && !ranks.has(itemId)) ranks.set(itemId, index + 1);
  });
  return ranks;
}

/**
 * Uncertainty multiplier per execution recipe, for a scenario that accounts for
 * uncertainty through work types rather than a flat capacity buffer.
 *
 * Returns undefined under the 'buffer' basis — nothing reads it there, and not
 * fetching keeps a buffer scenario's run exactly as it was.
 */
async function recipeMultipliersForScenario(
  scenario: Scenario,
): Promise<ReadonlyMap<string, number> | undefined> {
  if (scenario.uncertaintyBasis !== 'work_type') return undefined;
  const sb = getSupabaseClient();
  const { data } = await sb
    .from('execution_recipe_templates')
    .select('id, uncertainty_multiplier')
    .eq('active', true);
  const map = new Map<string, number>();
  for (const row of (data ?? []) as { id: string; uncertainty_multiplier: number | null }[]) {
    const m = Number(row.uncertainty_multiplier);
    map.set(row.id, Number.isFinite(m) && m >= 1 ? m : 1);
  }
  return map;
}

/**
 * Run the engine against a scoped item set.
 *
 * The assembly lives in `buildEngineInputScoped`; this is the one deterministic
 * pass over it, with the viability bands attached.
 */
export async function runEngineScoped(
  args: ScopedEngineRunArgs,
): Promise<PlanningEngineOutput> {
  return runPlanningEngineWithBands(await buildEngineInputScoped(args));
}

/**
 * Save a browser-computed engine run — ONE server transaction
 * (save_planning_run, migration 756). The engine still runs here; the save
 * upserts the planning_runs instance row (one per project + scenario + input
 * source) and appends a planning_run_history row carrying the same payload
 * (full output included) atomically. Authority is the planning_runs RLS rule
 * (a member of the workspace); the saver is the signed-in person, so
 * `createdBy` is accepted for call-site compatibility but not sent.
 */
export async function upsertPlanningRun(args: {
  projectId: string;
  scenarioId: string;
  engineInput: EngineInputSource;
  scopedItemIds: string[] | null;
  output: PlanningEngineOutput;
  createdBy?: string | null;
}): Promise<PlanningRunRecord> {
  const { projectId, scenarioId, engineInput, scopedItemIds, output } = args;
  const inputSourceId = engineInput.type === 'live' ? null : engineInput.id;
  const inputSourceName = engineInput.type === 'live' ? 'All Live Items' : engineInput.name;

  const { data, error } = await getSupabaseClient().rpc('save_planning_run', {
    p_project_id: projectId,
    p_scenario_id: scenarioId,
    p_input_source_type: engineInput.type,
    p_input_source_id: inputSourceId,
    p_input_source_name: inputSourceName,
    p_scoped_item_ids: scopedItemIds,
    p_output: output,
  });
  if (error) throw error;
  const result = (data ?? {}) as { success?: boolean; error?: string; run?: Record<string, unknown> };
  if (!result.success || !result.run) {
    throw new Error(result.error ?? 'The planning run was not saved.');
  }
  return toPlanningRun(result.run);
}

export async function getPlanningRun(
  projectId: string,
  scenarioId: string,
  engineInput: EngineInputSource,
): Promise<PlanningRunRecord | null> {
  const sb = getSupabaseClient();
  let query = sb
    .from('planning_runs')
    .select('*')
    .eq('project_id', projectId)
    .eq('scenario_id', scenarioId)
    .eq('input_source_type', engineInput.type)
    .eq('run_status', 'completed');

  query =
    engineInput.type === 'live'
      ? query.is('input_source_id', null)
      : query.eq('input_source_id', engineInput.id);

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  if (!data?.output) return null;
  return toPlanningRun(data as Record<string, unknown>);
}

// ---------------------------------------------------------------------------
// Run history — immutable log, distinct from the overwritten planning_runs instance
// ---------------------------------------------------------------------------

export interface PlanningRunHistoryEntry {
  id: string;
  projectId: string;
  scenarioId: string;
  inputSourceType: EngineInputSource['type'];
  inputSourceId: string | null;
  inputSourceName: string | null;
  scopedItemCount: number | null;
  executiveSummary: ExecutiveSummary;
  runStatus: string;
  runStartedAt: string;
  runCompletedAt: string | null;
  createdAt: string;
  /** The planning_runs instance row this run was saved into (756+; null before). */
  planningRunId: string | null;
  /**
   * True when the full engine output was kept (756+), so the run can be made
   * the active result again (restore_planning_run, 769).
   */
  hasSavedResults: boolean;
}

function toPlanningRunHistoryEntry(row: Record<string, unknown>): PlanningRunHistoryEntry {
  const scopedIds = row.scoped_item_ids as string[] | null;
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    scenarioId: row.scenario_id as string,
    inputSourceType: row.input_source_type as EngineInputSource['type'],
    inputSourceId: (row.input_source_id as string | null) ?? null,
    inputSourceName: (row.input_source_name as string | null) ?? null,
    scopedItemCount: scopedIds?.length ?? null,
    executiveSummary: row.executive_summary as ExecutiveSummary,
    runStatus: row.run_status as string,
    runStartedAt: row.run_started_at as string,
    runCompletedAt: (row.run_completed_at as string | null) ?? null,
    createdAt: row.created_at as string,
    planningRunId: (row.planning_run_id as string | null) ?? null,
    hasSavedResults: row.output != null && typeof row.output === 'object',
  };
}

/** Most-recent-first log of every run for a scenario (all input scopes). */
export async function getPlanningRunHistory(
  projectId: string,
  scenarioId: string,
  limit = 100,
): Promise<PlanningRunHistoryEntry[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_run_history')
    .select('*')
    .eq('project_id', projectId)
    .eq('scenario_id', scenarioId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((row) => toPlanningRunHistoryEntry(row as Record<string, unknown>));
}

export async function listPlanningRuns(
  projectId: string,
  scenarioId: string,
): Promise<PlanningRunRecord[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_runs')
    .select('*')
    .eq('project_id', projectId)
    .eq('scenario_id', scenarioId)
    .eq('run_status', 'completed')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? [])
    .filter((row) => row.output != null)
    .map((row) => toPlanningRun(row as Record<string, unknown>));
}

/** @internal exported for tests / debugging */
export const _LIVE_SCOPE_SENTINEL = LIVE_SCOPE_SENTINEL;
